import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, setDoc, getDoc, onSnapshot, collection, query, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';

// Default coaching pointer from system/config
const DEFAULT_POINTER_FALLBACK = "press on any questionmark icon to learn more about how ionoff works";

export interface ApiMetadata {
  endpoint: string;
  model: string;
  provider: string;
  temperature: number;
  maxOutputTokens: number | null; // null = no limit applied (using model default)
  costUSD: number;
  timestamp: string;
}

interface CoachingResponse {
  success: boolean;
  pointer?: string;
  coachingId?: string;
  modelUsed?: string;
  costUSD?: number;
  fullPrompt?: string;
  apiMetadata?: ApiMetadata;
  error?: string;
}

interface CoachingHistoryItem {
  id: string;
  pointer: string;
  fullPrompt?: string;
  apiMetadata?: ApiMetadata;
  timestamp: Date;
  conversationId?: string;
}

interface UseCoachingReturn {
  /** The current coaching pointer text (from history navigation or newly generated) */
  pointer: string | null;
  /** The full prompt that was sent to the API */
  fullPrompt: string | null;
  /** API metadata (endpoint, model, config) for the current pointer */
  apiMetadata: ApiMetadata | null;
  /** Whether coaching is currently being generated */
  isLoading: boolean;
  /** Whether coaching is enabled for the user */
  isEnabled: boolean;
  /** Toggle coaching on/off for the user */
  toggleCoaching: () => Promise<void>;
  /** Generate a new coaching pointer. Returns cost info for ionoff cost calculation. */
  generateCoaching: (
    conversationId: string,
    promptId: string,
    promptContent: string,
    deviceType: 'desktop' | 'mobile'
  ) => Promise<{ promptId: string; costUSD: number } | null>;
  /** Clear the current pointer */
  clearPointer: () => void;
  /** Navigate to previous coaching pointer in history */
  goToPrevious: () => void;
  /** Navigate to next coaching pointer in history */
  goToNext: () => void;
  /** Whether there's a previous pointer to navigate to */
  hasPrevious: boolean;
  /** Whether there's a next pointer to navigate to */
  hasNext: boolean;
  /** Whether history is still loading */
  isHistoryLoading: boolean;
}

/**
 * Hook to manage coaching functionality
 * 
 * Handles:
 * - User's coaching preference (enabled/disabled)
 * - Generating coaching pointers via Cloud Function
 * - Managing loading/error states
 * - Navigation through coaching history
 */
export function useCoaching(): UseCoachingReturn {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  
  // Default pointer from system/config
  const [defaultPointer, setDefaultPointer] = useState<string>(DEFAULT_POINTER_FALLBACK);
  
  // Coaching history state
  const [history, setHistory] = useState<CoachingHistoryItem[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(-1);
  const hasInitializedRef = useRef(false);
  const previousHistoryLengthRef = useRef(0);
  
  // Currently displayed pointer (from history or newly generated)
  const currentItem = currentHistoryIndex >= 0 && currentHistoryIndex < history.length
    ? history[currentHistoryIndex]
    : null;
  
  // Use history pointer, or fall back to default if no history
  const pointer = currentItem?.pointer ?? (history.length === 0 ? defaultPointer : null);
  const fullPrompt = currentItem?.fullPrompt ?? null;
  const apiMetadata = currentItem?.apiMetadata ?? null;
  
  // Fetch default coaching pointer from system/config
  useEffect(() => {
    const fetchDefaultPointer = async () => {
      try {
        const configRef = doc(db, 'system', 'config');
        const configDoc = await getDoc(configRef);
        
        if (configDoc.exists()) {
          const data = configDoc.data();
          if (data?.coaching_pointer1) {
            setDefaultPointer(data.coaching_pointer1);
            console.log('🎯 Loaded default coaching pointer from system/config');
          }
        }
      } catch (error) {
        console.error('Error fetching default coaching pointer:', error);
        // Keep using fallback
      }
    };
    
    fetchDefaultPointer();
  }, []);
  
  // Load user's coaching preference
  useEffect(() => {
    if (!user) {
      setIsEnabled(false); // Default to disabled
      return;
    }
    
    const userRef = doc(db, 'users', user.uid);
    
    // Subscribe to user document for real-time updates
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      const data = snapshot.data();
      // Default to disabled if not explicitly set
      setIsEnabled(data?.coachingEnabled === true);
    }, (error) => {
      console.error('Error listening to coaching preference:', error);
      setIsEnabled(false); // Default to disabled on error
    });
    
    return () => unsubscribe();
  }, [user]);
  
  // Subscribe to coaching history
  useEffect(() => {
    if (!user) {
      setHistory([]);
      setCurrentHistoryIndex(-1);
      setIsHistoryLoading(false);
      hasInitializedRef.current = false;
      return;
    }
    
    setIsHistoryLoading(true);
    // Reset initialization flag when user changes
    hasInitializedRef.current = false;
    
    const historyRef = collection(db, 'users', user.uid, 'coachingHistory');
    const historyQuery = query(
      historyRef,
      orderBy('timestamp', 'desc'),
      firestoreLimit(100) // Limit to last 100 coaching pointers
    );
    
    const unsubscribe = onSnapshot(historyQuery, (snapshot) => {
      const items: CoachingHistoryItem[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          pointer: data.pointer || '',
          fullPrompt: data.fullPrompt,
          apiMetadata: data.apiMetadata,
          timestamp: data.timestamp?.toDate() || new Date(),
          conversationId: data.conversationId,
        };
      });
      
      // Items are ordered desc (newest first), reverse for chronological order
      // We want oldest at index 0, newest at the end
      const chronologicalItems = items.reverse();
      
      setHistory(chronologicalItems);
      
      // Set to the most recent (last) item on initial load
      if (!hasInitializedRef.current && chronologicalItems.length > 0) {
        console.log(`🎯 Initial coaching history load - setting index to: ${chronologicalItems.length - 1}`);
        setCurrentHistoryIndex(chronologicalItems.length - 1);
        hasInitializedRef.current = true;
      } else if (chronologicalItems.length === 0) {
        setCurrentHistoryIndex(-1);
      } else {
        console.log(`🎯 Coaching history update - ${chronologicalItems.length} items, index unchanged`);
      }
      
      setIsHistoryLoading(false);
      
      // Debug: Log the actual pointer content
      if (chronologicalItems.length > 0) {
        const latestPointer = chronologicalItems[chronologicalItems.length - 1].pointer;
        console.log(`🎯 Latest pointer: "${latestPointer.substring(0, 50)}..."`);
      }
    }, (error) => {
      console.error('Error loading coaching history:', error);
      setIsHistoryLoading(false);
    });
    
    return () => unsubscribe();
  }, [user]);
  
  // When new item is added to history, move to it (only when history.length increases)
  useEffect(() => {
    const prevLength = previousHistoryLengthRef.current;
    const newLength = history.length;
    
    // Only act when history.length actually increases (new item added)
    if (newLength > prevLength && prevLength > 0 && !isLoading) {
      // A new item was added - jump to it if we were at the end
      const wasAtEnd = currentHistoryIndex === prevLength - 1;
      if (wasAtEnd) {
        console.log(`🎯 New coaching pointer added - jumping from ${currentHistoryIndex} to ${newLength - 1}`);
        setCurrentHistoryIndex(newLength - 1);
      }
    }
    
    // Update the ref for next comparison
    previousHistoryLengthRef.current = newLength;
  }, [history.length, currentHistoryIndex, isLoading]);
  
  /**
   * Toggle coaching on/off for the user
   */
  const toggleCoaching = useCallback(async () => {
    if (!user) return;
    
    const newValue = !isEnabled;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        coachingEnabled: newValue,
      }, { merge: true });
      
      // Optimistically update local state
      setIsEnabled(newValue);
      
      console.log(`🎯 Coaching ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling coaching:', error);
    }
  }, [user, isEnabled]);
  
  /**
   * Generate a new coaching pointer
   * Returns the cost (if successful) for ionoff cost calculation
   */
  const generateCoaching = useCallback(async (
    conversationId: string,
    promptId: string,
    promptContent: string,
    deviceType: 'desktop' | 'mobile'
  ): Promise<{ promptId: string; costUSD: number } | null> => {
    if (!user || !isEnabled) {
      return null;
    }
    
    setIsLoading(true);
    
    try {
      const functions = getFunctions();
      const generateCoachingPointer = httpsCallable<
        {
          userId: string;
          conversationId: string;
          triggerPromptId: string;
          currentPromptContent: string;
          deviceType: string;
        },
        CoachingResponse
      >(functions, 'generateCoachingPointer');
      
      const result = await generateCoachingPointer({
        userId: user.uid,
        conversationId,
        triggerPromptId: promptId,
        currentPromptContent: promptContent,
        deviceType,
      });
      
      if (result.data.success && result.data.pointer) {
        console.log('🎯 Coaching pointer received:', result.data.pointer.substring(0, 50) + '...');
        // The new pointer will be added to history via the onSnapshot listener
        // and we'll automatically navigate to it
        // Move to the end of history when new pointer is generated
        setCurrentHistoryIndex(history.length); // Will be length after new item is added
        
        // Return cost for ionoff cost calculation (to subtract from delta)
        const coachingCost = result.data.costUSD || 0;
        console.log('💰 Coaching cost for ionoff calculation:', coachingCost);
        return { promptId, costUSD: coachingCost };
      } else {
        console.warn('⚠️ Coaching generation failed:', result.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error generating coaching:', error);
      // Don't show error to user - coaching failure shouldn't disrupt UX
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, isEnabled, history.length]);
  
  /**
   * Clear the current pointer (reset to no selection)
   */
  const clearPointer = useCallback(() => {
    // Don't actually clear - just stay at current position
    // This was previously used to clear between prompts, but now we want persistence
  }, []);
  
  /**
   * Navigate to previous coaching pointer
   */
  const goToPrevious = useCallback(() => {
    console.log('🎯 goToPrevious called', { currentHistoryIndex, historyLength: history.length });
    if (currentHistoryIndex > 0) {
      setCurrentHistoryIndex(prev => {
        console.log('🎯 Setting index from', prev, 'to', prev - 1);
        return prev - 1;
      });
    } else {
      console.log('🎯 Cannot go previous - already at index 0');
    }
  }, [currentHistoryIndex, history.length]);
  
  /**
   * Navigate to next coaching pointer
   */
  const goToNext = useCallback(() => {
    if (currentHistoryIndex < history.length - 1) {
      setCurrentHistoryIndex(prev => prev + 1);
    }
  }, [currentHistoryIndex, history.length]);
  
  const hasPrevious = currentHistoryIndex > 0;
  const hasNext = currentHistoryIndex < history.length - 1;
  
  return {
    pointer,
    fullPrompt,
    apiMetadata,
    isLoading,
    isEnabled,
    toggleCoaching,
    generateCoaching,
    clearPointer,
    goToPrevious,
    goToNext,
    hasPrevious,
    hasNext,
    isHistoryLoading,
  };
}
